import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  PackageBranchSummary,
  PackageBuyer,
  PackageBuyersSummary,
  PackageBuyerStatus,
  PackageListData,
  PackageType,
} from './model'

type PackageListApiResponse = {
  data: Record<string, unknown>[]
  pagination: { total: number; page: number; pageSize: number }
}

export interface PackageBuyersParams {
  page?: number
  pageSize?: number
  query?: string
  start?: string
  end?: string
  status?: PackageBuyerStatus
}

export const PackageService = (client: AxiosInstance) => {
  return {
    async getPackageList(
      opt: PaginationOpt,
      packageTypeId?: string,
      isActive?: boolean,
      opts?: { branchIdOverride?: number }
    ): Promise<PaginationResponse<PackageListData>> {
      const { data } = await wrapApiCall(() => {
        const query = {
          ...opt,
          isActive,
          ...(packageTypeId ? { packageTypeId } : {}),
        }
        // The BE scopes this list by the `branch-id` header (all-branches
        // packages + the header branch's own). Callers that pick a branch
        // explicitly — the bundle builder (QTY-2065) — override the sidebar's
        // header for this one request so the list matches the chosen branch.
        const headers =
          opts?.branchIdOverride != null
            ? { 'branch-id': String(opts.branchIdOverride) }
            : undefined
        return client.get<PackageListApiResponse>('/packages', { params: query, headers })
      })
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, PackageListData)),
      }
    },
    async getPackageType(opt: PaginationOpt): Promise<PackageType[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          '/packages/types',
          { params: opt }
        )
      )
      const rawTypes: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : []
      return rawTypes.map((item) => deserialize(item, PackageType))
    },
    async getPackageById(packageId: number | string): Promise<PackageListData> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/packages/${packageId}`)
      )
      return deserialize(data, PackageListData)
    },
    async updatePackageById(
      packageId: number | string,
      updateData: Partial<PackageListData>
    ): Promise<PackageListData> {
      // Strip server-derived `status` before PATCH — it's a read-only field.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { status, ...rest } = updateData
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/packages/${packageId}`, rest)
      )
      return deserialize(data, PackageListData)
    },
    async createPackage(
      createData: PackageListData,
      opts?: { branchIdOverride?: number }
    ): Promise<PackageListData> {
      // When the create form (multi-branch tenant) has a branch picker
      // selected, override the global axios `branch-id` header for this
      // single request — BE uses it to write the PackageBranch pivot row.
      // Default header (sidebar's selection) flows when no override is set.
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/packages`, createData.serializeForCreate(), config)
      )
      return deserialize(data, PackageListData)
    },
    async getPackageBranches(packageId: number | string): Promise<PackageBranchSummary[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          `/packages/${packageId}/branches`
        )
      )
      const rawBranches: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : []
      return rawBranches.map((item) => deserialize(item, PackageBranchSummary))
    },
    async setPackageBranches(packageId: number | string, branchIds: number[]): Promise<void> {
      // Replaces the package's branch allow-list (PackageBranch pivot rows).
      // The BE also flips `isAllBranches` off in the same transaction, so a
      // non-empty list atomically makes the package branch-specific.
      await wrapApiCall(() => client.post(`/packages/${packageId}/branches`, { branchIds }))
    },
    async setPackageAllBranches(packageId: number | string, isAllBranches: boolean): Promise<void> {
      // Toggles the package's all-branches flag. When set to true the BE also
      // clears any PackageBranch pivot rows in the same transaction.
      await wrapApiCall(() =>
        client.patch(`/packages/${packageId}/all-branches`, { isAllBranches })
      )
    },
    async deletePackageById(packageId: number | string): Promise<void> {
      await wrapApiCall(() => client.delete(`/packages/${packageId}`))
    },
    async getPackageBuyers(
      packageId: number | string,
      params: PackageBuyersParams = {}
    ): Promise<PaginationResponse<PackageBuyer>> {
      const { data } = await wrapApiCall(() =>
        client.get<PackageListApiResponse>(`/packages/${packageId}/buyers`, { params })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, PackageBuyer)),
      }
    },
    async getPackageBuyersSummary(
      packageId: number | string,
      params: { start?: string; end?: string } = {}
    ): Promise<PackageBuyersSummary> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/packages/${packageId}/buyers/summary`, { params })
      )
      return deserialize(data, PackageBuyersSummary)
    },
  }
}
